import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import toast from "react-hot-toast";
import { BsArrowLeft } from "react-icons/bs";
import ClipLoader from "react-spinners/ClipLoader";
import OtpPin from "../../../../components/OtpPin";
import { API } from "../../../../constants/api";
import useAuthAxios from "../../../../utils/baseAxios";
import useIdempotencyKey from "../../../../hooks/useIdempotencyKey";
import { errorMessage } from "../../../../utils/errorMessage";
import { formatAmount } from "../../../../utils/formatTime";
import GetRates from "../../../../components/utils/GetRates";
import { useUser } from "../../../../context/user-context";
import { invalidateActivityQueries } from "../../../../utils/invalidateActivityQueries";

const BettingPreview = ({
  setOpenBetting,
  setOpenBettingPreview,
  betAmount,
  customerId,
  betId,
  customerName,
  providerName,
  paymentSource,
  setOpenBettingSuccess,
}: any) => {
  const axiosInstance = useAuthAxios();
  const idempotency = useIdempotencyKey();
  const queryClient = useQueryClient();
  const { refetch } = useUser();
  const [addPin, setAddPin] = useState<boolean>(false);
  const [otp, setOtp] = useState<string>("");
  const handleChange = (otp: React.SetStateAction<string>) => {
    setOtp(otp);
  };

  const fundBetting = async (data: any) => {
    const response = await axiosInstance.post(
      API.fundAza,
      data,
      idempotency.config()
    );
    return response.data;
  };
  const completeFund = useMutation({
    mutationFn: fundBetting,
    onSuccess: (r) => {
      idempotency.rotate();
      toast.success(r?.message ?? "Account funded successfully");
      invalidateActivityQueries(queryClient);
      queryClient.invalidateQueries({ queryKey: ["userDetails"] });
      queryClient.invalidateQueries({
        queryKey: ["naira-wallet-transactions"],
      });
      refetch();
      // Keep the form state — BettingSuccess reads it; Betting's back button clears it.
      setTimeout(() => {
        setOpenBettingPreview(false);
        setOpenBettingSuccess(true);
      }, 2000);
    },
    onError: (error: { data?: { message?: string }; message?: string }) => {
      idempotency.rotateIfAnswered(error);
      toast.error(
        errorMessage(error?.data?.message || error?.message || "Funding failed")
      );
    },
  });
  const usdtRate = GetRates("USDT");

  return (
    <div className="fixed inset-0  flex font-manrope justify-start items-start pt-16 overflow-auto pb-16 bg-[#E7EBEE] dark:bg-primary_dark dark:bg-opacity-95  backdrop-blur-sm">
      <div
        className={` w-11/12 mds:w-8/12 md:7/12 border dark:border-[#303030] bg-white  rounded-xl mx-auto p-6 dark:bg-[#192025]   lgss:w-2/5 xxl:w-1/3 `}
      >
        <div className="w-full flex justify-start gap-6 items-center">
          <button
            onClick={() => {
              setOpenBettingPreview(false);
              setOpenBetting(true);
            }}
          >
            <BsArrowLeft className="text-black dark:text-white text-[20px]" />
          </button>
          <h4 className="text-gray-900 dark:text-gray-100 font-sora font-semibold text-[20px]">
            Preview Betting
          </h4>
        </div>
        {addPin ? (
          <div className="mt-12">
            <h4 className="text-gray-900 text-center dark:text-gray-100 font-sora mb-4 font-semibold text-[20px]">
              Enter transaction PIN
            </h4>
            <OtpPin otp={otp} input={4} setOtp={setOtp} />
            <button
              disabled={completeFund.isPending || otp.length < 4}
              onClick={() => {
                const data = {
                  bet_id: betId,
                  customer_id: customerId,
                  amount: Number(betAmount),
                  customer_name: customerName,
                  pin: otp,
                  paymentSource,
                };
                completeFund.mutate(data);
              }}
              className="mt-12 bg-secondary_button flex justify-center items-center text-white text-[15px] font-medium h-[48px] w-full rounded-xl"
            >
              {completeFund.isPending ? (
                <ClipLoader color="#FFFFFF" size={30} />
              ) : (
                "Fund Account"
              )}
            </button>
          </div>
        ) : (
          <div className="w-full mt-5">
            <div className=" border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[13px] font-bold">
                Amount
              </h4>
              <h4 className=" mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {formatAmount(betAmount)} NGN
              </h4>
              {paymentSource === "USDT" && (
                <h4 className=" mt-2 text-[12px] font-bold text-[#BE6B0A]">
                  -
                  {betAmount && usdtRate
                    ? formatAmount(Number(betAmount) / Number(usdtRate))
                    : "0.00"}{" "}
                  USDT
                </h4>
              )}
            </div>
            <div className=" border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[13px] font-bold">
                Wallet
              </h4>
              <h4 className=" mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {paymentSource === "NGN"
                  ? "Naira Wallet (NGN)"
                  : "Tether (USDT)"}
              </h4>
            </div>
            <div className=" border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[13px] font-bold">
                Provider
              </h4>
              <div className="flex justify-start gap-2 mt-2 items-center">
                {/* <div className="flex justify-center items-center  w-[24px] h-[24px] rounded-full">
                  <img
                    src={buyNet?.logo}
                    className="w-full h-full bg-cover"
                    alt=""
                  />
                </div> */}
                <h4 className="  text-[16px] text-[#13181B] dark:text-gray-100 uppercase">
                  {providerName}
                </h4>
              </div>
            </div>
            <div className=" border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[13px] font-bold">
                Customer ID
              </h4>
              <h4 className=" mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {customerId}
              </h4>
            </div>
            <div className=" border-b border-gray-200 dark:border-gray-600 py-5">
              <h4 className="text-secondary_button text-[13px] font-bold">
                Customer name
              </h4>
              <h4 className=" mt-2 text-[16px] text-[#13181B] dark:text-gray-100">
                {customerName}
              </h4>
            </div>
            <button
              onClick={() => {
                // setOpenPreview(false);
                // setOpenSellPending(true);
                setAddPin(true);
              }}
              className="mt-12 bg-secondary_button flex justify-center items-center text-white text-[15px] font-medium h-[52px] w-full rounded-xl"
            >
              Proceed
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BettingPreview;
